import maplibregl from "maplibre-gl";

import { STOP_LABEL_COLLISION } from "../../../../config/global.configuration";
import type { StopLabelPlacementConfig } from "../stopLabels";
import {
    arePlacementConfigsEqual,
    buildTransitPathStopEntriesFingerprint,
    computeLabelScreenRect,
    rectsOverlap,
    resolveStopLabelPlacementConfig,
} from "./transitPathStopCollision.tools";
import type {
    ScreenRect,
    TransitPathStopCollisionEntry,
    TransitPathStopCollisionOptions,
} from "./transitPathStopCollision.types";

/**
 * MapLibre rounds marker screen coordinates to whole pixels on every `moveend`.
 * Vehicle follow uses `jumpTo` each frame, which fires `moveend` immediately — unlike
 * a continuous drag where only `move` events run until the gesture ends.
 * Subpixel positioning keeps dots and labels smooth during programmatic camera updates.
 */
const TRANSIT_PATH_STOP_MARKER_SUBPIXEL_OPTIONS = { "subpixelPositioning": true } as const;

interface MountedStopLabel {

    /** Source entry for this mounted stop label. */
    "entry": TransitPathStopCollisionEntry;

    /** Stable ordering index assigned at mount time. */
    "sequenceIndex": number;

    /** Optional always-visible dot marker. */
    "dotMarker": maplibregl.Marker | null;

    /** Label marker, when the entry has label content. */
    "labelMarker": maplibregl.Marker | null;

    /** Wrapper element passed to the label marker. */
    "labelWrapper": HTMLElement | null;

    /** Last placement config applied to the label marker. */
    "appliedConfig": StopLabelPlacementConfig | null;

    /** Last collision visibility applied to the label wrapper. */
    "isLabelVisible": boolean | null;

    /** Active opacity animation on the label wrapper, when a fade is running. */
    "labelOpacityAnimation": Animation | null;
}

/**
 * Manages HTML stop dots and labels on a MapLibre map with screen-space collision.
 */
export class TransitPathStopCollisionController {

    private readonly map: maplibregl.Map;

    private readonly textPadding: number;

    private readonly viewportRefreshThrottleMs: number;

    private readonly labelFadeDurationMs: number;

    private readonly mounted: MountedStopLabel[] = [];

    private entriesFingerprint = "";

    private throttleTimer: ReturnType<typeof setTimeout> | null = null;

    private collisionRafId: number | null = null;

    private lastCollisionPassAtMs = 0;

    private destroyed = false;

    /**
     * Creates a collision controller bound to a map instance.
     *
     * @param map Active MapLibre map.
     * @param options Optional collision tuning overrides.
     */
    constructor( map: maplibregl.Map, options?: TransitPathStopCollisionOptions ) {
        this.map = map;
        this.textPadding = options?.textPadding ?? STOP_LABEL_COLLISION.textPadding;
        this.viewportRefreshThrottleMs = options?.viewportRefreshThrottleMs ??
          STOP_LABEL_COLLISION.viewportRefreshThrottleMs;
        this.labelFadeDurationMs = resolveStopLabelFadeDurationMs( options?.labelFadeDurationMs );

        this.map.on( "move", this.handleMoveOrZoom );
        this.map.on( "zoom", this.handleMoveOrZoom );
        this.map.on( "moveend", this.handleMoveEndOrZoomEnd );
        this.map.on( "zoomend", this.handleMoveEndOrZoomEnd );
        this.map.on( "resize", this.handleResize );
    }

    /**
     * Replaces all managed stop labels and dots, then schedules a collision pass.
     *
     * @param entries Stop label entries to render.
     * @param fingerprintPrefix Optional prefix prepended to the fingerprint.
     */
    setEntries( entries: readonly TransitPathStopCollisionEntry[], fingerprintPrefix = "" ): void {
        const entriesFingerprint = buildTransitPathStopEntriesFingerprint( entries );
        const nextFingerprint = fingerprintPrefix.length > 0
            ? `${ fingerprintPrefix };${ entriesFingerprint }`
            : entriesFingerprint;
        if ( nextFingerprint === this.entriesFingerprint && this.mounted.length > 0 ) {
            this.scheduleCollisionPass();

            return;
        }

        this.entriesFingerprint = nextFingerprint;
        this.removeAllMarkers();

        const pendingMounts: MountedStopLabel[] = entries.map( ( entry, sequenceIndex ) => {
            let labelWrapper: HTMLElement | null = null;

            if ( entry.labelElement !== undefined && entry.labelConfig !== undefined ) {
                labelWrapper = this.createLabelWrapper( entry.labelElement );
            }

            return {
                entry,
                sequenceIndex,
                "dotMarker":             null,
                "labelMarker":           null,
                labelWrapper,
                "appliedConfig":         entry.labelConfig ?? null,
                "isLabelVisible":        null,
                "labelOpacityAnimation": null,
            };
        } );

        for ( const mounted of pendingMounts ) {
            if ( mounted.entry.dotElement === undefined ) {
                continue;
            }

            mounted.dotMarker = new maplibregl.Marker( {
                ...TRANSIT_PATH_STOP_MARKER_SUBPIXEL_OPTIONS,
                "anchor":  "center",
                "element": mounted.entry.dotElement,
            } ).
                setLngLat( mounted.entry.lngLat ).
                addTo( this.map );
        }

        for ( const mounted of pendingMounts ) {
            if ( mounted.labelWrapper === null || mounted.entry.labelConfig === undefined ) {
                continue;
            }

            mounted.labelMarker = new maplibregl.Marker( {
                ...TRANSIT_PATH_STOP_MARKER_SUBPIXEL_OPTIONS,
                "anchor":  mounted.entry.labelConfig.anchor,
                "element": mounted.labelWrapper,
                "offset":  mounted.entry.labelConfig.offset,
            } ).
                setLngLat( mounted.entry.lngLat ).
                addTo( this.map );
        }

        this.mounted.push( ...pendingMounts );

        this.scheduleCollisionPass();
    }

    /**
     * Removes all markers and detaches map listeners.
     */
    destroy(): void {
        if ( this.destroyed ) {
            return;
        }

        this.destroyed = true;
        this.map.off( "move", this.handleMoveOrZoom );
        this.map.off( "zoom", this.handleMoveOrZoom );
        this.map.off( "moveend", this.handleMoveEndOrZoomEnd );
        this.map.off( "zoomend", this.handleMoveEndOrZoomEnd );
        this.map.off( "resize", this.handleResize );
        this.clearThrottleTimer();
        this.clearCollisionRaf();
        this.removeAllMarkers();
        this.entriesFingerprint = "";
    }

    private readonly handleMoveOrZoom = (): void => {
        this.scheduleThrottledRefresh();
    };

    private readonly handleMoveEndOrZoomEnd = (): void => {
        this.clearThrottleTimer();
        this.scheduleCollisionPass();
    };

    private readonly handleResize = (): void => {
        this.scheduleCollisionPass();
    };

    private scheduleThrottledRefresh(): void {
        if ( this.destroyed ) {
            return;
        }

        const elapsedMs = Date.now() - this.lastCollisionPassAtMs;
        if ( elapsedMs >= this.viewportRefreshThrottleMs ) {
            this.runCollisionPass();

            return;
        }

        if ( this.throttleTimer !== null ) {
            return;
        }

        const remainingMs = this.viewportRefreshThrottleMs - elapsedMs;
        this.throttleTimer = setTimeout( () => {
            this.throttleTimer = null;
            this.runCollisionPass();
        }, remainingMs );
    }

    private scheduleCollisionPass(): void {
        if ( this.destroyed ) {
            return;
        }

        if ( this.collisionRafId !== null ) {
            cancelAnimationFrame( this.collisionRafId );
        }

        this.collisionRafId = requestAnimationFrame( () => {
            this.collisionRafId = requestAnimationFrame( () => {
                this.collisionRafId = null;
                this.runCollisionPass();
            } );
        } );
    }

    private runCollisionPass(): void {
        if ( this.destroyed || this.mounted.length === 0 ) {
            return;
        }

        this.lastCollisionPassAtMs = Date.now();

        const sortedLabels = this.mounted.
            filter( ( mounted ) => mounted.labelMarker !== null && mounted.labelWrapper !== null ).
            sort( ( left, right ) => {
                if ( right.entry.priority !== left.entry.priority ) {
                    return right.entry.priority - left.entry.priority;
                }

                return left.sequenceIndex - right.sequenceIndex;
            } );

        const placedRects: ScreenRect[] = [];

        for ( const mounted of sortedLabels ) {
            const { entry, labelMarker, labelWrapper } = mounted;
            if ( labelMarker === null || labelWrapper === null || entry.labelElement === undefined ) {
                continue;
            }

            const { width, height } = this.measureLabelSize( entry.labelElement );

            if ( width <= 0 || height <= 0 ) {
                this.setLabelVisible( mounted, false );

                continue;
            }

            const projected = this.map.project( entry.lngLat );
            const config = resolveStopLabelPlacementConfig( entry );
            const candidateRect = computeLabelScreenRect(
                projected.x,
                projected.y,
                width,
                height,
                config.anchor,
                config.offset,
                this.textPadding,
            );

            const overlapsExisting = placedRects.some( ( placedRect ) => rectsOverlap( placedRect, candidateRect ) );
            if ( overlapsExisting ) {
                this.setLabelVisible( mounted, false );

                continue;
            }

            this.applyLabelPlacement( mounted, config );
            this.setLabelVisible( mounted, true );
            placedRects.push( candidateRect );
        }
    }

    private applyLabelPlacement(
        mounted: MountedStopLabel,
        config: StopLabelPlacementConfig,
    ): void {
        const { labelMarker, labelWrapper, entry } = mounted;
        if ( labelMarker === null || labelWrapper === null ) {
            return;
        }

        if (
            mounted.appliedConfig !== null &&
            arePlacementConfigsEqual( mounted.appliedConfig, config )
        ) {
            return;
        }

        const lngLat = labelMarker.getLngLat();
        labelMarker.remove();

        const replacementMarker = new maplibregl.Marker( {
            ...TRANSIT_PATH_STOP_MARKER_SUBPIXEL_OPTIONS,
            "anchor":  config.anchor,
            "element": labelWrapper,
            "offset":  config.offset,
        } ).
            setLngLat( lngLat ).
            addTo( this.map );

        mounted.labelMarker = replacementMarker;
        mounted.appliedConfig = config;
        entry.labelConfig = config;
    }

    private createLabelWrapper( labelElement: HTMLElement ): HTMLElement {
        const wrapper = document.createElement( "div" );
        wrapper.className = "pointer-events-none";
        wrapper.style.opacity = "0";
        wrapper.style.visibility = "hidden";

        wrapper.appendChild( labelElement );

        return wrapper;
    }

    private measureLabelSize( labelElement: HTMLElement ): {
        "width":  number;
        "height": number;
    } {
        let width = labelElement.offsetWidth;
        let height = labelElement.offsetHeight;
        if ( width > 0 && height > 0 ) {
            return {
                width,
                height,
            };
        }

        const rect = labelElement.getBoundingClientRect();
        width = Math.ceil( rect.width );
        height = Math.ceil( rect.height );
        if ( width > 0 && height > 0 ) {
            return {
                width,
                height,
            };
        }

        const clone = labelElement.cloneNode( true ) as HTMLElement;
        clone.style.cssText = [
            "position:absolute",
            "left:-9999px",
            "top:0",
            "visibility:hidden",
            "opacity:1",
            "pointer-events:none",
        ].join( ";" );
        document.body.appendChild( clone );
        width = clone.offsetWidth;
        height = clone.offsetHeight;
        document.body.removeChild( clone );

        return {
            width,
            height,
        };
    }

    private setLabelVisible( mounted: MountedStopLabel, isVisible: boolean ): void {
        if ( mounted.labelWrapper === null || mounted.isLabelVisible === isVisible ) {
            return;
        }

        const wrapper = mounted.labelWrapper;
        this.clearLabelAnimation( mounted );
        mounted.isLabelVisible = isVisible;

        if ( this.labelFadeDurationMs <= 0 ) {
            wrapper.style.visibility = isVisible ? "visible" : "hidden";
            wrapper.style.opacity = isVisible ? "1" : "0";

            return;
        }

        if ( isVisible ) {
            wrapper.style.visibility = "visible";
            const startOpacity = this.readElementOpacity( wrapper );

            const animation = wrapper.animate(
                [
                    { "opacity": startOpacity },
                    { "opacity": 1 },
                ],
                {
                    "duration": this.labelFadeDurationMs,
                    "easing":   "ease",
                    "fill":     "forwards",
                },
            );
            mounted.labelOpacityAnimation = animation;
            animation.onfinish = () => {
                if ( mounted.labelOpacityAnimation !== animation ) {
                    return;
                }

                wrapper.style.opacity = "1";
                mounted.labelOpacityAnimation = null;
            };

            return;
        }

        const startOpacity = this.readElementOpacity( wrapper );
        const fromOpacity = startOpacity > 0 ? startOpacity : 1;

        const animation = wrapper.animate(
            [
                { "opacity": fromOpacity },
                { "opacity": 0 },
            ],
            {
                "duration": this.labelFadeDurationMs,
                "easing":   "ease",
                "fill":     "forwards",
            },
        );
        mounted.labelOpacityAnimation = animation;
        animation.onfinish = () => {
            if ( mounted.labelOpacityAnimation !== animation || mounted.isLabelVisible !== false ) {
                return;
            }

            wrapper.style.visibility = "hidden";
            wrapper.style.opacity = "0";
            mounted.labelOpacityAnimation = null;
        };
    }

    private readElementOpacity( element: HTMLElement ): number {
        const parsedOpacity = Number.parseFloat( getComputedStyle( element ).opacity );

        return Number.isFinite( parsedOpacity ) ? parsedOpacity : 0;
    }

    private clearLabelAnimation( mounted: MountedStopLabel ): void {
        if ( mounted.labelOpacityAnimation !== null ) {
            mounted.labelOpacityAnimation.cancel();
            mounted.labelOpacityAnimation = null;
        }
    }

    private removeAllMarkers(): void {
        for ( const mounted of this.mounted ) {
            this.clearLabelAnimation( mounted );
            mounted.dotMarker?.remove();
            mounted.labelMarker?.remove();
        }

        this.mounted.length = 0;
    }

    private clearThrottleTimer(): void {
        if ( this.throttleTimer !== null ) {
            clearTimeout( this.throttleTimer );
            this.throttleTimer = null;
        }
    }

    private clearCollisionRaf(): void {
        if ( this.collisionRafId !== null ) {
            cancelAnimationFrame( this.collisionRafId );
            this.collisionRafId = null;
        }
    }

}

/**
 * Resolves label fade duration, honouring `prefers-reduced-motion` and config defaults.
 *
 * @param override Optional constructor override (ms).
 * @returns Fade duration in milliseconds (`0` = instant).
 */
function resolveStopLabelFadeDurationMs( override?: number ): number {
    const configuredDurationMs = override ?? STOP_LABEL_COLLISION.labelFadeDurationMs;
    if ( configuredDurationMs <= 0 ) {
        return 0;
    }

    if ( typeof window !== "undefined" &&
      window.matchMedia( "(prefers-reduced-motion: reduce)" ).matches ) {
        return 0;
    }

    return configuredDurationMs;
}
